import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getBlogPostBySlug } from '../../content/blogPosts';
import usePageMeta from '../../hooks/usePageMeta';
import useLangPrefix from '../../hooks/useLangPrefix';
import './Blog.css';

function Blocks({ blocks }) {
  return blocks.map((block, i) => {
    if (block.type === 'list') {
      return (
        <ul className="mb-3" key={i}>
          {block.items.map((item, j) => (
            <li key={j}>{item}</li>
          ))}
        </ul>
      );
    }
    return (
      <p className="mb-3" key={i}>
        {block.text}
      </p>
    );
  });
}

function BlogPost() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const langPrefix = useLangPrefix();
  const post = getBlogPostBySlug(slug);

  const content = post && (post[i18n.language] || post.ar);
  const isFallback = post && !post[i18n.language];
  const dir = isFallback ? 'rtl' : undefined;

  usePageMeta(
    content
      ? {
          title: content.seoTitle,
          description: content.metaDescription,
          keywords: content.keywords,
          path: `${langPrefix}/blog/${slug}`,
        }
      : {}
  );

  // BlogPosting/FAQPage JSON-LD is baked into build/blog/<slug>/index.html by
  // scripts/prerender-seo.js — no need to inject it again client-side.

  if (!post) {
    return <Navigate to={`${langPrefix}/blog`} replace />;
  }

  const formattedDate = new Date(post.date).toLocaleDateString(
    i18n.language === 'ar' ? 'ar-LB' : 'en-US',
    { year: 'numeric', month: 'long', day: 'numeric' }
  );

  return (
    <article className="py-5 py-lg-6 bg-white" dir={dir}>
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            <Link to={`${langPrefix}/blog`} className="link-navy text-decoration-none small d-inline-block mb-4">
              {t('blog.backToList')}
            </Link>

            <p className="text-muted small mb-2">{formattedDate}</p>
            <h1 className="h2 fw-semibold text-navy mb-4">{content.title}</h1>

            {content.intro.map((p, i) => (
              <p className="mb-3" key={i}>
                {p}
              </p>
            ))}

            {content.sections.map((section, i) => (
              <section className="mt-4" key={i}>
                <h2 className="h4 fw-semibold text-navy mb-3">{section.heading}</h2>
                <Blocks blocks={section.blocks} />
                {section.subsections &&
                  section.subsections.map((sub, j) => (
                    <div className="mt-3" key={j}>
                      <h3 className="h6 fw-semibold text-navy mb-2">{sub.heading}</h3>
                      <Blocks blocks={sub.blocks} />
                    </div>
                  ))}
                {section.cta && (
                  <Link to={`${langPrefix}/contact`} className="btn btn-navy mt-2">
                    {t('nav.contact')}
                  </Link>
                )}
              </section>
            ))}

            {content.closingNote && (
              <p className="text-muted small fst-italic mt-5 mb-0">{content.closingNote}</p>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}

export default BlogPost;
